import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useProjectDetailStore } from 'store/local/projectDetail';
import MuiButton from 'components/common/button/MuiButton';
import { T } from 'components/common/text';
import { color } from 'styles/utils';
import cx from 'classnames';
import RefreshIcon from '@material-ui/icons/Refresh';
import { ProjectContext } from 'contexts/project/ProjectContext';
import { AppActions } from 'store/actionCreators';
import BrowserIcon from 'components/base/icons/BrowserIcon';
import { useTranslation } from 'react-i18next';
import { useProject } from 'lib/hooks/project/useProject';
import ProductionRequestIcon from 'components/base/icons/ProductionRequestIcon';
import ClinicModal from 'components/common/modal/ClinicModal';
import ProductionRequestViewer from './ProductionRequestViewer';
import { useProjectFiles } from 'lib/hooks/project/useProjectFiles';

const scanfileTypes = ['.obj', '.ply', '.stl'];
function ProjectContentsExtra() {
  const { ipcRenderer } = window;
  const { t } = useTranslation();

  // SECTION: state

  const { currentTab, projectId } = useProjectDetailStore();
  const { hasAlarm, projectCode, scanFileExists } = useProject();
  const { handleClearAllView } = useProjectFiles();
  const [productionRequestOpen, setProductionRequestOpen] = useState<boolean>(false);

  const { onClickOpenWorkspace, scanFileListCt, onLoadClinicLocalFiles, classifyClinicFiles } =
    useContext(ProjectContext);

  // SECTION: variable

  // SECTION: declare hooks

  // SECTION: declare function

  const handleClickFileImport = () => {
    if (!ipcRenderer) return;
    ipcRenderer.send('importFile', {
      directoryType: 'SCAN_WORKSPACE',
    });
  };

  const handleOpenProductionRequest = () => {
    setProductionRequestOpen(true);
  };

  const handleCloseProductionRequest = () => {
    setProductionRequestOpen(false);
  };

  const handleClickWebViewer = () => {
    // console.log('HDS] scanFileExists _______ ', scanFileExists);
    if (scanFileExists === 1) {
      // ScanFile 존재
      const viewUrl = `${process.env.REACT_APP_WEB_VIEW_URL}/${projectCode}`;
      ipcRenderer.send('openBrowser', {
        url: viewUrl,
      });
    } else {
      // ScanFile 없음, 웹뷰어 사용불가
      AppActions.add_popup({
        isOpen: true,
        isCloseIcon: true,
        width: 660,
        title: '',
        content: <T>OPEN_FAIL_WEB_VIEW</T>,
        onClick() {
          return;
        },
        onCancel() {
          return;
        },
      });
    }
  };

  return (
    <StyledProjectContentsExtra data-component-name="ProjectContentsExtra">
      <div className="projectContentsExtra__container">
        <div
          className={cx('projectContentsExtra__box left', {
            visible: !!currentTab && !!ipcRenderer,
          })}
        >
          <ExtraButton
            disabled={hasAlarm}
            onClick={handleClickFileImport}
            name={t('FILE_IMPORT')}
            className="open"
          />
          <ExtraButton
            disabled={hasAlarm}
            onClick={() => {
              onClickOpenWorkspace(projectId);
            }}
            name={t('PROJECT_OPEN_WORKSPACE')}
            className="workspace"
          />

          <ExtraButton
            icon={
              <div className="refresh_icon">
                <RefreshIcon
                  htmlColor={!hasAlarm ? color.stage_completed : color.gray_b5}
                  fontSize="inherit"
                />
              </div>
            }
            disabled={hasAlarm}
            onClick={() => {
              handleClearAllView();
              onLoadClinicLocalFiles();
            }}
            className="refresh"
          />
        </div>

        {/*  */}
        <div className="projectContentsExtra__box right visible">
          <ExtraButton
            icon={<ProductionRequestIcon />}
            disabled={hasAlarm}
            onClick={handleOpenProductionRequest}
            name={t('PRODUCTION_REQUEST_OERDER')}
            className="productionRequest"
          />
          {/* <ExtraButton
            icon={<IDCardIcon />}
            disabled={hasAlarm}
            onClick={handleClickWebViewer}
            name={t('환자 리포트')}
            className="patientReport"
          /> */}
          <ExtraButton
            icon={<BrowserIcon />}
            disabled={hasAlarm}
            onClick={handleClickWebViewer}
            name={t('GLOBAL_SYNC_VIEWER')}
            className="webViewer"
          />
        </div>
      </div>
      <ClinicModal
        title={t('SAVE_PDF')}
        isOpen={productionRequestOpen}
        onClose={handleCloseProductionRequest}
        width={700}
      >
        <ProductionRequestViewer />
      </ClinicModal>
    </StyledProjectContentsExtra>
  );
}

const StyledProjectContentsExtra = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  height: 52px;
  border-left: 1px solid ${color.border_line};
  border-bottom: 1px solid ${color.border_line};
  width: calc(100% - 480px);
  z-index: 1;
  .projectContentsExtra__container {
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: space-between;
    padding: 5px 10px;
    .projectContentsExtra__box {
      height: 100%;
      visibility: hidden;
      &.visible {
        visibility: visible;
      }

      display: flex;
      align-items: center;
      column-gap: 5px;
    }
  }
`;

interface ExtraButtonProps {
  icon?: any;
  hoverIcon?: any;
  disabled: boolean;
  onClick: () => void;
  name?: string;
  className: string;
}

function ExtraButton({ icon, hoverIcon, disabled, onClick, name, className }: ExtraButtonProps) {
  const [hover, setHover] = useState<boolean>(false);

  return (
    <StyledExtraButton data-component-name="ExtraButton" $hover={hover} $disabled={disabled}>
      <MuiButton
        className={`extraButton__wrapper ${className}`}
        config={{
          borderColor: '#E1E1E7',
          bgColor: 'white',
        }}
        disabled={disabled}
        disableElevation
        variant="outlined"
        onClick={onClick}
        onMouseOver={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <div className="extraButton__box">
          <div className="extraButton__icon">
            {!!icon &&
              typeof icon !== 'string' &&
              React.cloneElement(icon, {
                color: !!disabled ? color.gray_b5 : !!hover ? '#00a6e2' : '#444',
              })}
            {!!icon && typeof icon === 'string' && (
              <>
                {!hover && <img src={icon} />}
                {!!hoverIcon && !!hover && <img src={hoverIcon} />}
              </>
            )}
          </div>
          {!!name && <span className="extraButton__name">{name}</span>}
        </div>
      </MuiButton>
    </StyledExtraButton>
  );
}

const StyledExtraButton = styled.div<{ $hover: boolean; $disabled: boolean }>`
  .extraButton__wrapper {
    /* width: 130px; */
    height: 38px;
    border-radius: 3px;
    padding: 0 5px;
  }
  .extraButton__box {
    display: flex;
    align-items: center;
    justify-content: center;
    column-gap: 6px;
    .extraButton__icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .extraButton__name {
      font-size: 14px;
      font-weight: 400;
      color: ${({ $hover, $disabled }) =>
        !!$disabled ? color.gray_b5 : !!$hover ? '#00a6e2' : '#444'};
    }
  }

  .refresh {
    width: 50px;
    font-size: 28px;
    .refresh_icon {
      display: flex;
      align-items: center;
      justify-content: center;
      transform: scaleX(-1);
    }
  }

  .webViewer {
    /* width: 100px; */
  }
`;

export default ProjectContentsExtra;
